import { BatteryCharging, CircleArrowUp, MapPin, Thermometer } from "lucide-react";
import { Link } from "react-router-dom";

import { formatNumber, timeAgo } from "@/lib/format";
import { batteryLevel, temperatureLevel, type Health } from "@/lib/health";
import { connectionSummary, describeConnection } from "@/lib/network";
import type { Device } from "@/lib/types";

import { Meter, StatusBadge, levelColor } from "./ui";

export function DeviceCard({
  device,
  health,
  groupName,
  overheatAt,
  outdated = false,
  now,
}: {
  device: Device;
  health: Health;
  groupName?: string;
  overheatAt: number;
  /** Runs an older agent than the newest release. */
  outdated?: boolean;
  /** Passed in so every card counts time from the same moment. */
  now: number;
}) {
  const telemetry = device.latest_telemetry;
  const battery = telemetry?.battery_percent ?? null;
  const temperature = telemetry?.temperature_c ?? null;
  const heat = temperatureLevel(temperature, overheatAt);
  // Readings from a device that is off describe the past, not the device.
  const stale = !device.is_online;
  const network = connectionSummary(describeConnection(telemetry));

  return (
    <Link
      to={`/devices/${device.id}`}
      className="card block p-4 transition-colors hover:bg-raised focus-visible:bg-raised"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="truncate font-semibold" title={device.name}>
            {device.name}
          </div>
          <div className="flex items-center gap-1 truncate text-[13px] text-ink-2">
            <MapPin size={12} className="shrink-0 text-muted" aria-hidden />
            <span className="truncate">
              {[device.shop_label, groupName].filter(Boolean).join(" · ") || "No shop assigned"}
            </span>
          </div>
        </div>
        <StatusBadge level={health.level} label={health.label} title={health.reasons.join("\n")} />
      </div>

      <dl className={stale ? "mt-4 grid grid-cols-2 gap-3 opacity-55" : "mt-4 grid grid-cols-2 gap-3"}>
        <div>
          <dt className="flex items-center gap-1 text-xs text-muted">
            Battery
            {telemetry?.battery_charging && (
              <BatteryCharging size={13} aria-label="charging" className="text-ink-2" />
            )}
          </dt>
          <dd className="mt-0.5">
            <span className="text-[17px] font-semibold">
              {battery === null ? "—" : `${formatNumber(battery)}%`}
            </span>
            <Meter
              value={battery}
              level={batteryLevel(battery, telemetry?.battery_charging)}
              label="Battery"
              className="mt-1.5"
            />
          </dd>
        </div>
        <div>
          <dt className="text-xs text-muted">Temperature</dt>
          <dd className="mt-0.5 flex items-center gap-1.5">
            <span className="text-[17px] font-semibold">
              {temperature === null ? "—" : `${formatNumber(temperature, 1)} °C`}
            </span>
            {(heat === "warning" || heat === "critical") && (
              <Thermometer
                size={15}
                style={{ color: levelColor(heat) }}
                aria-label={heat === "critical" ? "too hot" : "getting hot"}
              />
            )}
          </dd>
        </div>
      </dl>

      {health.reasons.length > 0 && (
        <p className="mt-3 truncate text-[13px] text-ink-2" title={health.reasons.join("\n")}>
          {health.reasons[0]}
          {health.reasons.length > 1 && ` (+${health.reasons.length - 1} more)`}
        </p>
      )}
      <p className="mt-3 flex flex-wrap items-center gap-x-2 text-xs text-muted">
        <span>
          {device.is_online ? "Heard from" : "Last seen"} {timeAgo(device.last_seen_at, now)}
        </span>
        {network && (
          <span className="truncate" title={telemetry?.local_ip ?? undefined}>
            · {network}
          </span>
        )}
        {outdated && (
          <span
            className="inline-flex items-center gap-1"
            title={`Agent ${device.agent_version}: an update is available`}
          >
            · <CircleArrowUp size={12} style={{ color: "var(--color-warning)" }} aria-hidden />
            Agent {device.agent_version}
          </span>
        )}
      </p>
    </Link>
  );
}
